import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import usePipeline from "@/features/stage/api/usePipeline";
import { resolvePickerSelection } from "@/features/stage/utils/impliedPipelineSelection";
import type { Stage, Status, SubStatus } from "@/features/stage/types/stageTypes";

/**
 * Quick header filter for Stage → Status → SubStatus, mirrors the visual
 * pattern of `BranchScopePicker` (pill trigger + popover list). Three
 * cascading MULTI-SELECT popovers, each one populates its list from the
 * parent selection — the same comma-list values the filter drawer writes, so
 * a drawer selection of five sub-statuses reads back here as "Sub: X +4"
 * instead of the pill pretending nothing is picked:
 *
 *   - Stage list: every stage in the pipeline (ordered by `Stage.order`).
 *   - Status list: statuses on the selected stage(s), prefixed with the stage
 *     name when more than one stage is picked. Empty if no stage picked.
 *   - SubStatus list: subStatuses on the selected status(es). Empty otherwise.
 *
 * `hideStagePicker` is for dynamic-stage routes (e.g. `/Qualify`) where the
 * URL already pins the stage — render only Status + SubStatus. Pass
 * `forcedStageId` so the Status options are still computed against that
 * stage even though the user can't change it from this picker.
 *
 * Cascading PRUNE: un-ticking a stage drops only the statuses / sub-statuses
 * that belonged to it (siblings under a still-selected stage survive); same
 * for statuses → sub-statuses. "All …" rows at the top of each popover clear
 * the slot only (keeping siblings intact). The parent's `onSelect` decides
 * whether to commit a single field or the cascaded triple.
 */

export type StageStatusKey = "stage" | "status" | "subStatus";

export interface StageStatusValue {
  stage: string;
  status: string;
  subStatus: string;
}

/**
 * Filter-aware pipeline counts for the three pickers (see
 * `usePipelineLeadStats`). `total` is the no-stage total ("All stage"); the
 * per-stage / status / sub maps are keyed by `String(id)`. Omit to render the
 * pickers without counts (e.g. while loading or on routes that don't fetch them).
 */
export interface PipelinePickerCounts {
  total: number;
  stageCounts?: Record<string, number>;
  statusCounts?: Record<string, number>;
  subStatusCounts?: Record<string, number>;
}

interface StageStatusPickerProps {
  value: StageStatusValue;
  /**
   * Called once per slot change. When stage / status changes the parent
   * should ALSO clear downstream slots — the picker emits a sequence of
   * onSelect calls in cascade order, then a final commit on the way out.
   */
  onSelect: (next: { key: StageStatusKey; value: string }) => void;
  /** Hide the Stage trigger when the route already pins a stage. */
  hideStagePicker?: boolean;
  /**
   * When `hideStagePicker` is true the picker still needs to know which
   * stage to derive the Status options from. Defaults to `value.stage` so
   * the leads-page flow doesn't need to pass it explicitly.
   */
  forcedStageId?: string | number | null;
  /** Filter-aware counts shown next to each stage / status / sub option. */
  counts?: PipelinePickerCounts;
  /**
   * A branch whose OWN pipeline the options come from (branch pipelines). Left
   * out → the default pipeline, exactly as before.
   */
  pipelineBranchId?: number;
}

const triggerClass =
  "inline-flex items-center gap-1.5 h-8 shrink-0 rounded-lg border border-[#e6e8e7] bg-[#f7f9f8] px-2.5 hover:bg-[#eef1f0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-success)]/30 transition-colors cursor-pointer disabled:opacity-60";
const labelClass =
  "text-[12px] font-medium tracking-[-0.24px] text-[#374741] truncate max-w-[140px]";
const mutedLabelClass =
  "text-[12px] font-medium tracking-[-0.24px] text-[#9ba3a0] truncate max-w-[140px]";

function PickerRow({
  label,
  count,
  checked,
  onSelect,
  muted,
}: {
  label: string;
  /** Optional facet count shown on the right (e.g. leads per country). */
  count?: number;
  checked: boolean;
  onSelect: () => void;
  muted?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-[13px] cursor-pointer hover:bg-[var(--color-black-1)] focus-visible:bg-[var(--color-black-1)] focus-visible:outline-none transition-colors ${muted ? "text-[var(--color-text-gray)]" : ""}`}
    >
      <span className="truncate flex-1">{label}</span>
      <span className="flex items-center gap-2 shrink-0">
        {count !== undefined && (
          <span className="text-muted-foreground tabular-nums text-[12px]">
            {count.toLocaleString()}
          </span>
        )}
        {checked && (
          <Check
            className="size-4 text-[var(--color-success)]"
            strokeWidth={2}
          />
        )}
      </span>
    </button>
  );
}

export function SinglePickerPopover({
  triggerLabel,
  placeholder,
  disabled,
  options,
  selectedId,
  onPick,
  searchPlaceholder,
  emptyHint,
  counts,
  totalCount,
}: {
  triggerLabel: string | null;
  placeholder: string;
  disabled?: boolean;
  options: Array<{ id: number | string; name: string }>;
  selectedId: string;
  onPick: (id: string) => void;
  searchPlaceholder: string;
  emptyHint: string;
  /**
   * Optional per-option facet counts keyed by `String(option.id)`. When
   * provided, each row shows its count on the right (e.g. leads per country),
   * "swinging" with the active filters. Omit for plain pickers (stage/status).
   */
  counts?: Record<string, number>;
  /** Count shown next to the "All …" reset row when `counts` is provided. */
  totalCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={triggerClass}>
          <span className={triggerLabel ? labelClass : mutedLabelClass}>
            {triggerLabel ?? placeholder}
          </span>
          <ChevronDown
            className="size-4 text-[var(--color-text-secondary)] shrink-0"
            strokeWidth={1.75}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="p-0 w-[220px] max-w-[16rem] overscroll-contain"
        // Same cap as the header's branch picker: ~16 rows before it scrolls,
        // never past the space Radix measured under the trigger.
        style={{
          maxHeight:
            "min(var(--radix-popover-content-available-height, 80vh), 80vh, 40rem)",
          overflowY: "auto",
        }}
      >
        <div className="sticky top-0 z-10 border-b border-[var(--color-black-2)] bg-popover p-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px]"
            autoFocus
          />
        </div>

        <div className="p-1">
          <PickerRow
            label={`All ${placeholder.replace("Pick a ", "").replace("Pick ", "")}`}
            count={counts ? totalCount : undefined}
            checked={selectedId === ""}
            muted
            onSelect={() => {
              onPick("");
              setOpen(false);
            }}
          />
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--color-text-gray)]">
              {emptyHint}
            </p>
          ) : (
            filtered.map((o) => (
              <PickerRow
                key={o.id}
                label={o.name}
                count={counts ? (counts[String(o.id)] ?? 0) : undefined}
                checked={String(o.id) === selectedId}
                onSelect={() => {
                  onPick(String(o.id));
                  setOpen(false);
                }}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const parseList = (v: string): string[] =>
  v
    ? String(v)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

/** "Status: Offer Pending +1" — first picked name, then how many more. */
function multiLabel(
  prefix: string,
  picked: Array<{ name: string }>,
): string | null {
  if (picked.length === 0) return null;
  const first = picked[0]!.name;
  return picked.length > 1
    ? `${prefix}: ${first} +${picked.length - 1}`
    : `${prefix}: ${first}`;
}

/**
 * Multi-select twin of {@link SinglePickerPopover}. Rows TOGGLE and the
 * popover stays open so several can be ticked in one visit; the "All …" row
 * clears the slot and closes. The parent applies on every change (this is the
 * quick picker — the drawer is where you stage a batch and Apply once).
 */
export function MultiPickerPopover({
  triggerLabel,
  placeholder,
  disabled,
  options,
  selectedIds,
  onChange,
  searchPlaceholder,
  emptyHint,
  counts,
  totalCount,
}: {
  triggerLabel: string | null;
  placeholder: string;
  disabled?: boolean;
  options: Array<{ id: number | string; name: string }>;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  searchPlaceholder: string;
  emptyHint: string;
  counts?: Record<string, number>;
  totalCount?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  const toggle = (id: string) =>
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} className={triggerClass}>
          <span className={triggerLabel ? labelClass : mutedLabelClass}>
            {triggerLabel ?? placeholder}
          </span>
          <ChevronDown
            className="size-4 text-[var(--color-text-secondary)] shrink-0"
            strokeWidth={1.75}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="p-0 w-[240px] max-w-[18rem] overscroll-contain"
        style={{ maxHeight: "min(60vh, 22rem)", overflowY: "auto" }}
      >
        <div className="sticky top-0 z-10 border-b border-[var(--color-black-2)] bg-popover p-2">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[2px]"
            autoFocus
          />
        </div>

        <div className="p-1">
          <PickerRow
            label={`All ${placeholder.replace("Pick a ", "").replace("Pick ", "")}`}
            count={counts ? totalCount : undefined}
            checked={selectedIds.length === 0}
            muted
            onSelect={() => {
              onChange([]);
              setOpen(false);
            }}
          />
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-[var(--color-text-gray)]">
              {emptyHint}
            </p>
          ) : (
            filtered.map((o) => (
              <PickerRow
                key={o.id}
                label={o.name}
                count={counts ? (counts[String(o.id)] ?? 0) : undefined}
                checked={selectedIds.includes(String(o.id))}
                onSelect={() => toggle(String(o.id))}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function StageStatusPicker({
  value,
  onSelect,
  hideStagePicker = false,
  forcedStageId = null,
  counts,
  pipelineBranchId,
}: StageStatusPickerProps) {
  const { pipeline } = usePipeline(undefined, pipelineBranchId);

  const stages = useMemo<Stage[]>(() => {
    const list = (pipeline ?? []) as Stage[];
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [pipeline]);

  // Every slot is a comma list — the same values the filter drawer writes.
  const explicitStageIds =
    hideStagePicker && forcedStageId != null
      ? [String(forcedStageId)]
      : parseList(value.stage);
  const explicitStatusIds = parseList(value.status);
  const selectedSubStatusIds = parseList(value.subStatus);

  /**
   * A STATUS ALREADY NAMES ITS STAGE (see `impliedPipelineSelection`). A
   * filter set that arrives with a status but no stage — a Favorite View saved
   * from a stage route, a report drill ↗, a shared link — used to render "Pick
   * stage" empty and the Status pill DISABLED on "Pick a stage first", hiding
   * the very status that was filtering the list. Resolved for display and for
   * the option lists only; nothing is written back to the filter state.
   */
  const { stageIds: selectedStageIds, statusIds: selectedStatusIds } = useMemo(
    () =>
      resolvePickerSelection(stages as any, {
        stageIds: explicitStageIds,
        statusIds: explicitStatusIds,
        subStatusIds: selectedSubStatusIds,
      }),
    [
      stages,
      explicitStageIds.join(","),
      explicitStatusIds.join(","),
      selectedSubStatusIds.join(","),
    ],
  );

  const selectedStages = stages.filter((s) =>
    selectedStageIds.includes(String(s.id)),
  );
  // Statuses across EVERY selected stage, tagged with their stage so the
  // option label can disambiguate ("Application → Pending" vs "Visa → Pending")
  // once more than one stage is picked — same convention as the drawer.
  const statuses = selectedStages.flatMap((s) =>
    ((s.statuses ?? []) as Status[]).map((st) => ({ ...st, stageName: s.name })),
  );
  const selectedStatuses = statuses.filter((st) =>
    selectedStatusIds.includes(String(st.id)),
  );
  const subStatuses = selectedStatuses.flatMap((st) =>
    ((st.subStatuses ?? []) as SubStatus[]).map((ss) => ({
      ...ss,
      statusName: st.name,
    })),
  );
  const selectedSubStatuses = subStatuses.filter((ss) =>
    selectedSubStatusIds.includes(String(ss.id)),
  );

  const stageOptions = useMemo(
    () =>
      stages.map((s) => ({
        id: s.id,
        name: `${s.order}. ${s.name}`,
      })),
    [stages],
  );
  const multiStage = selectedStages.length > 1;
  const statusOptions = statuses.map((s) => ({
    id: s.id,
    name: multiStage ? `${s.stageName} → ${s.name}` : s.name,
  }));
  const multiStatus = selectedStatuses.length > 1;
  const subStatusOptions = subStatuses.map((s) => ({
    id: s.id,
    name: multiStatus ? `${s.statusName} → ${s.name}` : s.name,
  }));

  const sumCounts = (
    map: Record<string, number> | undefined,
    ids: string[],
  ): number | undefined => {
    if (!map) return undefined;
    return ids.reduce((acc, id) => acc + (map[id] ?? 0), 0);
  };

  const pickStages = (nextIds: string[]) => {
    onSelect({ key: "stage", value: nextIds.join(",") });
    // Cascade PRUNE (not clear): statuses whose stage is still selected
    // survive; only the ones that belonged to an un-ticked stage go, and
    // their sub-statuses with them. Emitted as explicit events so the parent
    // commits a consistent triple even if the user reopens mid-cascade.
    const validStatusIds = new Set(
      stages
        .filter((s) => nextIds.includes(String(s.id)))
        .flatMap((s) => (s.statuses ?? []).map((st) => String(st.id))),
    );
    const keptStatuses = selectedStatusIds.filter((id) => validStatusIds.has(id));
    if (keptStatuses.length !== selectedStatusIds.length) {
      onSelect({ key: "status", value: keptStatuses.join(",") });
    }
    const validSubIds = new Set(
      statuses
        .filter((st) => keptStatuses.includes(String(st.id)))
        .flatMap((st) => (st.subStatuses ?? []).map((ss) => String(ss.id))),
    );
    const keptSubs = selectedSubStatusIds.filter((id) => validSubIds.has(id));
    if (keptSubs.length !== selectedSubStatusIds.length) {
      onSelect({ key: "subStatus", value: keptSubs.join(",") });
    }
  };
  const pickStatuses = (nextIds: string[]) => {
    onSelect({ key: "status", value: nextIds.join(",") });
    const validSubIds = new Set(
      statuses
        .filter((st) => nextIds.includes(String(st.id)))
        .flatMap((st) => (st.subStatuses ?? []).map((ss) => String(ss.id))),
    );
    const keptSubs = selectedSubStatusIds.filter((id) => validSubIds.has(id));
    if (keptSubs.length !== selectedSubStatusIds.length) {
      onSelect({ key: "subStatus", value: keptSubs.join(",") });
    }
  };
  const pickSubStatuses = (nextIds: string[]) => {
    onSelect({ key: "subStatus", value: nextIds.join(",") });
  };

  const hasStage = selectedStageIds.length > 0;
  const hasStatus = selectedStatusIds.length > 0;

  return (
    <div className="flex items-center gap-2 min-w-0">
      {!hideStagePicker && (
        <MultiPickerPopover
          triggerLabel={multiLabel("Stage", selectedStages)}
          placeholder="Pick stage"
          options={stageOptions}
          selectedIds={selectedStageIds}
          onChange={pickStages}
          searchPlaceholder="Search stages…"
          emptyHint="No matching stages"
          counts={counts?.stageCounts}
          totalCount={counts?.total}
        />
      )}
      <MultiPickerPopover
        triggerLabel={multiLabel("Status", selectedStatuses)}
        placeholder="Pick status"
        disabled={!hasStage || statusOptions.length === 0}
        options={statusOptions}
        selectedIds={selectedStatusIds}
        onChange={pickStatuses}
        searchPlaceholder="Search statuses…"
        emptyHint={
          !hasStage ? "Pick a stage first" : "No statuses on this stage"
        }
        counts={counts?.statusCounts}
        // "All status" = leads in the selected stage(s) (roll-up of statuses).
        totalCount={
          hasStage
            ? sumCounts(counts?.stageCounts, selectedStageIds)
            : counts?.total
        }
      />
      <MultiPickerPopover
        triggerLabel={multiLabel("Sub", selectedSubStatuses)}
        placeholder="Pick sub-status"
        disabled={!hasStatus || subStatusOptions.length === 0}
        options={subStatusOptions}
        selectedIds={selectedSubStatusIds}
        onChange={pickSubStatuses}
        searchPlaceholder="Search sub-statuses…"
        emptyHint={
          !hasStatus ? "Pick a status first" : "No sub-statuses on this status"
        }
        counts={counts?.subStatusCounts}
        // "All sub-status" = leads in the selected stage(s) + status(es).
        totalCount={
          hasStatus
            ? sumCounts(counts?.statusCounts, selectedStatusIds)
            : hasStage
              ? sumCounts(counts?.stageCounts, selectedStageIds)
              : counts?.total
        }
      />
    </div>
  );
}
